import find from 'lodash-es/find';
import { MentionComponent } from '../components/mention/mention.component';
import {
  ComponentFactoryResolver,
  Injector,
  ApplicationRef,
  ComponentRef,
  ElementRef,
  ChangeDetectorRef,
  QueryList
} from '@angular/core';
import { SharedHelpers } from './sharedhelpers';
import { Participant } from '@mitel/cloudlink-sdk/chat';
import { User } from '@mitel/cloudlink-sdk/admin';
import { ChatCloudLinkService } from '../services/chat-cloudlink.service';
import { ChatDatabaseService } from '../services/chat-database.service';
import { ChatAdminService } from '../services/chat-admin.service';
import type { TextAreaProps } from '../components/chat/chat.component';
import { Menu } from 'primeng/menu';
import { ChatLoggingService } from '../services/chat-logging.service';
import { ChatAuthService } from '../services/chat-auth.service';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { KeyboardKeys } from './keyboard-keys';
import { MenuHelpers } from './menuhelpers';
import { EmojiPipe } from './../pipes/emoji.pipe';

export class MentionHelpers {
  /**
   * Get the root node for the cloudlink chat component. This is either the document or the shadow root,
   * in case the component has been loaded in a shadow DOM.
   */
  static getRootNode(textArea: ElementRef<HTMLElement>): Document | ShadowRoot {
    const root = textArea.nativeElement.getRootNode();
    if (root instanceof ShadowRoot || root instanceof Document) {
      return root;
    }
    // Not sure if this is possible, but let's return the document by default
    return document;
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////
  // Current state of the APIs as of Oct 2024:
  // - ShadowRoot.getSelection is a non-standard API.
  // - Selection.getComposedRanges is a standards proposal to support selection with Shadow DOM.
  //
  // On Chromium, calling document.getSelection will not pierce into the Shadow DOM and gives you
  // some unhelpful high-level element. However, it does expose the non-standard getSelection
  // method on the ShadowRoot.
  //
  // Firefox does not implement ShadowRoot.getSelection, but document.getSelection will pierce
  // through shadow dom and give you the exact element.
  //
  // Saferi does not support getSelection on the ShadowRoot, but does support getComposedRanges
  // on the Selection as of V17.
  /////////////////////////////////////////////////////////////////////////////////////////////////
  static getSelection(textArea: ElementRef<HTMLElement>): Selection {
    const rootNode = this.getRootNode(textArea);
    if (rootNode instanceof ShadowRoot && 'getSelection' in rootNode) {
      return (rootNode as any).getSelection();
    }
    return document.getSelection();
  }

  static getSelectionData(selection: Selection): string {
    return (selection?.anchorNode as any)?.data ?? '';
  }

  static getRange(textArea: ElementRef<HTMLElement>, selection: Selection): Range | undefined {
    if (!selection.rangeCount) {
      return undefined;
    }
    const rootNode = this.getRootNode(textArea);
    if (rootNode instanceof ShadowRoot && !('getSelection' in rootNode) && 'getComposedRanges' in selection) {
      const staticRange = (selection as any).getComposedRanges(rootNode)[0];
      const range = document.createRange();
      range.setStart(staticRange.startContainer, staticRange.startOffset);
      range.setEnd(staticRange.endContainer, staticRange.endOffset);
      return range;
    }
    return selection.getRangeAt(0);
  }

  static addRangeToSelection(range: Range, selection: Selection) {
    if (selection.setBaseAndExtent) {
      selection.setBaseAndExtent(range.startContainer, range.startOffset, range.endContainer, range.endOffset);
    } else {
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  // PrimeNG's popup Menu focuses its own list when shown, which swallows any further typing.
  // Call from the menu's (onShow) to move focus back to the text area, keeping the caret where it was.
  // Arrow/Enter/Escape for the dropdown are handled by the components' window keydown listeners.
  static restoreTextAreaFocus(textArea: ElementRef<HTMLElement>) {
    const element = textArea?.nativeElement;
    if (!element || this.getRootNode(textArea).activeElement === element) {
      return;
    }
    const selection = this.getSelection(textArea);
    const range = selection ? this.getRange(textArea, selection) : undefined;
    const savedRange = range && element.contains(range.startContainer) ? range.cloneRange() : undefined;
    element.focus();
    if (savedRange) {
      this.addRangeToSelection(savedRange, this.getSelection(textArea));
    }
  }

  static getFilteredMentions(mentions: string[]) {
    var newMentions = mentions.flatMap(m => {
      var mention = m.trim();
      //Is a GUID mention
      //Adjacent mentions without whitespace (e.g. "@guid1@guid2") match as one token, so return every GUID
      if (SharedHelpers.guidRegex.test(mention)) {
        return mention.match(SharedHelpers.guidRegexGlobal).map(guid => '@' + guid);
      }
      //Legacy email mention
      //need to check is input include email or not
      else if ((mention.indexOf('.') > -1 && SharedHelpers.emailRegex.test(mention)) || mention.includes('@here')) {
        //Let's just replace all special characters after the '.'
        let startIndex = 0;
        if (!mention.includes('@here')) {
          startIndex = mention.indexOf('@');
        }
        var startMention = mention.slice(startIndex, mention.lastIndexOf('.') + 1);
        var endMention = mention.slice(mention.lastIndexOf('.') + 1);
        //Remove lodash escape characters
        endMention = endMention.replace(/&quot/g, '');
        endMention = endMention.replace(/&#34/g, '');
        endMention = endMention.replace(/&gt/g, '');
        endMention = endMention.replace(/&#62/g, '');
        endMention = endMention.replace(/&lt/g, '');
        endMention = endMention.replace(/&#60/g, '');
        endMention = endMention.replace(/&amp/g, '');
        endMention = endMention.replace(/&#38/g, '');
        endMention = endMention.replace(/&apos/g, '');
        endMention = endMention.replace(/&#39/g, '');
        endMention = endMention.replace(/[^0-9a-zA-Z]/g, '');
        mention = startMention + endMention;
        if (mention === 'here') {
          return ['@' + mention];
        }
        return [mention];
      }
      return [];
    });
    return newMentions;
  }

  static replaceWithMentionPlaceholder(text: string, mention: string): string {
    var matchFull = true;
    if (SharedHelpers.guidRegex.test(mention) || text.indexOf('@here') != 0) {
      matchFull = false;
    }
    return SharedHelpers.replaceAll(
      text,
      mention,
      '<span class="mi-mention-placeholder" hidden="true" itemprop="' + mention + '"></span>',
      matchFull
    );
  }

  static removeMentionPlaceholder(text: string, mention: string): string {
    return SharedHelpers.replaceAll(
      text,
      SharedHelpers.escapeRegExp(
        '<span class="mi-mention-placeholder" hidden="true" itemprop="' + mention + '"></span>'
      ),
      mention,
      false
    );
  }

  static replaceWithMentionElement(
    mention: string,
    getElement: Function,
    resolver: ComponentFactoryResolver,
    injector: Injector,
    appRef: ApplicationRef
  ): {
    ref?: ComponentRef<MentionComponent>;
    placeholder: any;
    noMatch?: boolean;
  } {
    let mentions = getElement().nativeElement.querySelectorAll('.mi-mention-placeholder');
    let placeholder = find(mentions, m => {
      return m.hidden && m.getAttribute('itemprop') === mention;
    });
    if (placeholder) {
      placeholder.contentEditable = false;
      placeholder.hidden = false;
      let factory = resolver.resolveComponentFactory(MentionComponent);
      let ref = factory.create(injector, [], placeholder);
      ref.setInput('userId', mention.substring(1, mention.length));
      appRef.attachView(ref.hostView);
      return {
        ref: ref,
        placeholder: placeholder
      };
    }
    let noMatch = true;
    if (
      find(mentions, m => {
        return !m.hidden && m.getAttribute('itemprop') === mention;
      })
    ) {
      noMatch = false;
    }
    return {
      placeholder: placeholder,
      noMatch: noMatch
    };
  }

  static checkForMentions(
    key,
    textArea: ElementRef,
    textAreaProps: TextAreaProps,
    mentionsDropdown: Menu,
    chatCloudLinkService: ChatCloudLinkService,
    chatDatabaseService: ChatDatabaseService,
    chatAdminService: ChatAdminService,
    chatAuthService: ChatAuthService,
    cdRef: ChangeDetectorRef,
    participants: Participant[],
    hereString: string,
    commandsDropdown?: Menu,
    mentionsAnchor?: ElementRef,
    dropdownMaxHeight?: string
  ) {
    let foundAtSymbol = false;
    let selection = this.getSelection(textArea);
    if (SharedHelpers.isCharacterKeyPress(key)) {
      textAreaProps.mentionInput = '';
      if (this.getSelectionData(selection)) {
        //Find where the caret currently is
        textAreaProps.mentionCaretEnd = this.getRange(textArea, selection).startOffset;
        //If typing in the middle of the input, need to look ahead and move the caret position in case there's more text as part of the mention text
        //e.g. "Searching for Kevin" and then you type '@' before 'Kevin'
        const text = this.getSelectionData(selection);
        let endOfCaretIndex = text.substring(textAreaProps.mentionCaretEnd, text.length).indexOf(' ');
        if (endOfCaretIndex > -1) {
          textAreaProps.mentionCaretEnd += endOfCaretIndex;
        } else {
          textAreaProps.mentionCaretEnd += text.substring(textAreaProps.mentionCaretEnd, text.length).length;
        }
        //This is getting the first text to the first space looking backwards from the caret
        let textToCaret = text.substring(0, textAreaProps.mentionCaretEnd);
        let firstSpace = textToCaret.lastIndexOf(' ');
        if (firstSpace > -1) {
          let textToFirstSpace = textToCaret.substring(0, firstSpace);
          let firstSpaceText = textToCaret.substring(firstSpace + 1, textAreaProps.mentionCaretEnd);
          if (!foundAtSymbol && firstSpaceText.match(SharedHelpers.mentionDropdownRegex)) {
            foundAtSymbol = true;
            textAreaProps.mentionInput = firstSpaceText.substring(
              firstSpaceText.lastIndexOf('@') + 1,
              firstSpaceText.length
            );
          }
          //This is getting the second text from the first space from the previous step looking backwards until the start or a space (if applicable)
          //Needed for searching "@Kevin Lee" and searching for "Kevin Lee"
          let secondSpace = textToFirstSpace.lastIndexOf(' ');
          if (secondSpace > -1) {
            let secondSpaceText = textToCaret.substring(secondSpace + 1, firstSpace);
            if (!foundAtSymbol && secondSpaceText.match(SharedHelpers.mentionDropdownRegex)) {
              foundAtSymbol = true;
              textAreaProps.mentionInput =
                secondSpaceText.substring(secondSpaceText.lastIndexOf('@') + 1, secondSpaceText.length) +
                ' ' +
                firstSpaceText;
            }
          } else {
            let secondSpaceText = textToCaret.substring(0, firstSpace);
            if (!foundAtSymbol && secondSpaceText.match(SharedHelpers.mentionDropdownRegex)) {
              foundAtSymbol = true;
              textAreaProps.mentionInput =
                secondSpaceText.substring(secondSpaceText.lastIndexOf('@') + 1, secondSpaceText.length) +
                ' ' +
                firstSpaceText;
            }
          }
        } else {
          let firstSpaceText = textToCaret.substring(0, textAreaProps.mentionCaretEnd);
          if (!foundAtSymbol && firstSpaceText.match(SharedHelpers.mentionDropdownRegex)) {
            foundAtSymbol = true;
            textAreaProps.mentionInput = firstSpaceText.substring(
              firstSpaceText.lastIndexOf('@') + 1,
              firstSpaceText.length
            );
          }
        }
      }
    }
    if (foundAtSymbol) {
      textAreaProps.mentionNode = selection.focusNode;
      this.fetchMentions(
        textAreaProps.mentionInput,
        textArea,
        textAreaProps,
        mentionsDropdown,
        chatCloudLinkService,
        chatDatabaseService,
        chatAdminService,
        chatAuthService,
        cdRef,
        participants,
        hereString,
        commandsDropdown,
        mentionsAnchor,
        dropdownMaxHeight
      );
    } else if (SharedHelpers.isCharacterKeyPress(key)) {
      textAreaProps.mentionPending = false;
      if (mentionsDropdown) {
        mentionsDropdown.hide();
      }
    }
  }

  static async fetchMentions(
    username: string,
    textArea: ElementRef,
    textAreaProps: TextAreaProps,
    mentionsDropdown: Menu,
    chatCloudLinkService: ChatCloudLinkService,
    chatDatabaseService: ChatDatabaseService,
    chatAdminService: ChatAdminService,
    chatAuthService: ChatAuthService,
    cdRef: ChangeDetectorRef,
    participants: Participant[],
    hereString: string,
    commandsDropdown?: Menu,
    mentionsAnchor?: ElementRef,
    dropdownMaxHeight?: string
  ) {
    textAreaProps.mentionPending = true;
    textAreaProps.mentionFetchAttempt++;
    let fetchAttempt = textAreaProps.mentionFetchAttempt;

    if (username.trim() === '' && participants?.length) {
      let participantsToTake = participants.filter(p => p.userId !== chatAuthService.userId).slice(0, 50);
      let participantUsers = [];
      participantsToTake.forEach(p => {
        if (chatAdminService.displayNames[p.userId]) {
          participantUsers.push({
            userId: p.userId,
            displayName: chatAdminService.displayNames[p.userId],
            email: chatAdminService.emails[p.userId],
            avatars: chatAdminService.avatars[p.userId]
          });
        }
      });
      participantUsers = participantUsers.sort();
      textAreaProps.mentions = participantUsers;
    } else if (chatCloudLinkService.isOffline()) {
      let mentions = await chatDatabaseService.searchUsers(username);
      if (textAreaProps.mentionPending) {
        if (mentions.length) {
          textAreaProps.mentions = mentions;
        } else if (mentions && mentions.length === 0) {
          textAreaProps.mentions = [];
        }
        if (hereString.toLowerCase().indexOf(username) === 0) {
          textAreaProps.mentions.unshift({
            userId: 'here',
            displayName: '@' + hereString
          });
        }
      }
    } else {
      const resMentions = await chatCloudLinkService.admin.getUsers({
        skip: 0,
        top: 50,
        search: username,
        searchKeys: username ? 'name,email' : '',
        orderby: 'name'
      });

      let mentions = resMentions.body;
      //Old mention search, ignore
      if (fetchAttempt !== textAreaProps.mentionFetchAttempt) {
        return;
      }
      if (textAreaProps.mentionPending) {
        if (mentions._embedded && mentions._embedded.items) {
          textAreaProps.mentions = mentions._embedded.items;
        } else if (mentions && mentions.count === 0) {
          textAreaProps.mentions = [];
        }
        if (hereString.toLowerCase().indexOf(username) === 0) {
          textAreaProps.mentions.unshift({
            userId: 'here',
            displayName: '@' + hereString
          });
        }
      }
    }
    if (textAreaProps.mentionPending) {
      textAreaProps.commandPending = false;
      if (commandsDropdown) {
        commandsDropdown.hide();
      }
      if (mentionsDropdown) {
        let caretCoordinates = this.getCaretCoordinates(
          textArea.nativeElement,
          textAreaProps.lastCaretPos,
          chatCloudLinkService
        );
        //position dropdown close to the cursor coordinates
        let isEditTextArea = textArea.nativeElement.classList.contains('edit-text-area');
        textAreaProps.mentionDropdownMargins.bottom = -caretCoordinates.top;
        //adjust when scrollbar is in text area
        if (textArea?.nativeElement?.scrollTop) {
          textAreaProps.mentionDropdownMargins.bottom += textArea.nativeElement.scrollTop;
        }
        textAreaProps.mentionDropdownMargins.left = isEditTextArea ? caretCoordinates.left - 32 : caretCoordinates.left;
        //position dropdown horizontally in limited space
        if (textArea?.nativeElement?.clientWidth) {
          let dropdownWidth = 450;
          if (textArea?.nativeElement?.clientWidth - caretCoordinates.left < dropdownWidth) {
            textAreaProps.mentionDropdownMargins.left = isEditTextArea
              ? textArea?.nativeElement?.clientWidth - dropdownWidth
              : textArea?.nativeElement?.clientWidth - dropdownWidth + 32;
          }
        }

        if (mentionsAnchor?.nativeElement && textArea?.nativeElement) {
          this.setDropdownMaxHeight('mentions', dropdownMaxHeight);
          MenuHelpers.showAbove(mentionsDropdown, textArea.nativeElement, { align: 'left' });
        }
      }
    }
    //Only want mentions with display names
    textAreaProps.mentions = textAreaProps.mentions.filter(m => m.displayName);
    textAreaProps.selectedDropdownIndex = 0;
    textAreaProps.mentionPending = false;
    if (textAreaProps.mentions?.length === 0) {
      if (mentionsDropdown) {
        mentionsDropdown.hide();
      }
    }
    cdRef.detectChanges();
  }

  //For https://github.com/mitel-networks/cloudlink-components-chat/issues/1250
  static waitForMentionsLoaded(
    loadedMentionObservables: Subject<void>[],
    ngUnsubscribe: Subject<void>,
    cdRef: ChangeDetectorRef,
    textArea: ElementRef<any>,
    textAreaProps: TextAreaProps,
    chatLoggingService: ChatLoggingService,
    chatCloudLinkService: ChatCloudLinkService,
    componentName: string,
    setCaret: Function,
    setMessageFn: Function,
    setToEnd: boolean
  ) {
    //This bug doesn't exist on Mac products and the execCommands don't seem to store on the undo stack on Mac browsers
    if (
      chatCloudLinkService.browser.os.toLowerCase() === 'ios' ||
      chatCloudLinkService.browser.os.toLowerCase() === 'mac os'
    ) {
      return;
    }

    //Fixes bug where caret isn't properly set with mentions
    if (loadedMentionObservables.length) {
      forkJoin(loadedMentionObservables)
        .pipe(take(1), takeUntil(ngUnsubscribe))
        .subscribe(() => {
          cdRef.detectChanges();
          setTimeout(() => {
            if (!setToEnd) {
              this.getCaretPosition(textArea, textAreaProps, chatCloudLinkService, chatLoggingService, componentName);
            }
            setCaret();

            textArea.nativeElement.focus();

            document.execCommand('selectAll', false, null);
            document.execCommand('delete', false, null);
            //Note that the above commands don't seem to add onto the undo stack for Mac browsers
            document.execCommand('undo', false, null);

            if (setMessageFn) {
              setMessageFn(textArea.nativeElement.innerText);
            }

            if (setToEnd) {
              setCaret();
            } else {
              this.setCaretPosition(textArea, textAreaProps);
            }
          });
        });
    }
  }

  static enterMention(
    mention: User,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    mentionsDropdown: Menu,
    textAreaProps: TextAreaProps,
    textArea: ElementRef<any>,
    mentionRefs: any[],
    resolver: ComponentFactoryResolver,
    injector: Injector,
    appRef: ApplicationRef,
    cdRef: ChangeDetectorRef,
    setMessageFn?: Function
  ): Subject<void>[] {
    chatLoggingService.info({
      logMessage: componentName + '.enterMention()',
      logParameters: [mention?.userId]
    });
    var textNode = textAreaProps.mentionNode;
    var node = textArea.nativeElement;
    node.focus();
    var range = document.createRange();
    range.setStart(textNode, textAreaProps.mentionCaretEnd - (textAreaProps.mentionInput.length + 1));
    range.setEnd(textNode, textAreaProps.mentionCaretEnd);
    var sel = this.getSelection(textArea);
    this.addRangeToSelection(range, sel);

    //principalId comes via the cache
    let userId = mention.userId || (<any>mention).principalId;

    document.execCommand(
      'InsertHTML',
      false,
      '<span class="mi-mention-placeholder" hidden="true" itemprop="@' + userId + '"></span>'
    );

    let loadedMentionObservables = [];
    var getElement = () => {
      return textArea;
    };
    let mentionReturn = this.replaceWithMentionElement('@' + userId, getElement, resolver, injector, appRef);
    if (mentionReturn.ref) {
      mentionRefs.push(mentionReturn.ref);
      loadedMentionObservables.push(mentionReturn.ref.instance.loaded$);
    }
    let placeholder = mentionReturn.placeholder;

    setTimeout(() => {
      var textChild = document.createTextNode(' ');
      placeholder.after(textChild);
      var range = document.createRange();
      range.setStart(textChild, 1);
      range.setEnd(textChild, 1);
      var sel = this.getSelection(textArea);
      this.addRangeToSelection(range, sel);
      if (setMessageFn) {
        setMessageFn(textArea.nativeElement.innerText);
      }
      cdRef.detectChanges();
    }, 0);
    if (mentionsDropdown) {
      mentionsDropdown.hide();
    }
    return loadedMentionObservables;
  }

  static isMentionsDropdownOpen(mentionsDropdown: Menu, textAreaProps: TextAreaProps) {
    return (
      mentionsDropdown &&
      mentionsDropdown.overlayVisible &&
      textAreaProps.mentions?.length &&
      textAreaProps.mentions[textAreaProps.selectedDropdownIndex]
    );
  }

  static setDropdownIndex(
    index: number,
    textAreaProps: TextAreaProps,
    mentionsDropdown: Menu,
    dropdownMentions: QueryList<ElementRef>,
    cdRef: ChangeDetectorRef,
    commandsDropdown?: Menu,
    dropdownCommands?: QueryList<ElementRef>
  ) {
    let count = 0;
    if (commandsDropdown?.overlayVisible && textAreaProps.commands?.length) {
      count = textAreaProps.commands.length;
    } else if (mentionsDropdown && mentionsDropdown.overlayVisible && textAreaProps.mentions?.length) {
      count = textAreaProps.mentions.length;
    }
    if (count) {
      if (index > count - 1) {
        textAreaProps.selectedDropdownIndex = 0;
      } else if (index < 0) {
        textAreaProps.selectedDropdownIndex = count - 1;
      } else {
        textAreaProps.selectedDropdownIndex = index;
      }
      cdRef.detectChanges();
      let resultEl: ElementRef = null;
      if (commandsDropdown?.overlayVisible && dropdownCommands) {
        resultEl = dropdownCommands.filter((item: ElementRef, index: number) => {
          return index === textAreaProps.selectedDropdownIndex;
        })[0];
      } else if (mentionsDropdown && mentionsDropdown.overlayVisible && dropdownMentions) {
        resultEl = dropdownMentions.filter((item: ElementRef, index: number) => {
          return index === textAreaProps.selectedDropdownIndex;
        })[0];
      }
      if (resultEl?.nativeElement && resultEl?.nativeElement?.parentElement) {
        //Can't use scrollIntoView as it causes the message area to scroll when doing edit message
        //resultEl?.nativeElement?.scrollIntoView();
        let paddingTop = 0;
        if ((<any>mentionsDropdown)?._menu?.nativeElement) {
          paddingTop = parseFloat(getComputedStyle((<any>mentionsDropdown)?._menu?.nativeElement).paddingTop);
        }
        resultEl.nativeElement.parentElement.scrollTop = resultEl.nativeElement.offsetTop - paddingTop;
      }
    }
  }

  static isBackspaceMention(key: any, code: any, textAreaProps: TextAreaProps) {
    return (
      (key === KeyboardKeys.BACKSPACE || code === KeyboardKeys.DELETE || code === KeyboardKeys.BACKSPACE) &&
      (textAreaProps?.lastNode?.className === 'mi-mention-placeholder' ||
        textAreaProps?.lastNode?.parentNode?.className === 'mi-mention-placeholder' ||
        textAreaProps?.lastNode?.parentNode?.parentNode?.className === 'mi-mention-placeholder')
    );
  }

  static deleteBackspaceMention(
    chatCloudLinkService: ChatCloudLinkService,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    textAreaProps: TextAreaProps,
    textArea: ElementRef<any>
  ) {
    chatLoggingService.debug({
      logMessage: componentName + '.keyUpSubject() found backspace mention'
    });
    var mentionNode = textAreaProps.lastNode;
    if (textAreaProps?.lastNode?.className === 'mi-mention-placeholder') {
      mentionNode = textAreaProps.lastNode;
    } else if (textAreaProps?.lastNode?.parentNode?.className === 'mi-mention-placeholder') {
      mentionNode = textAreaProps.lastNode.parentNode;
    } else if (textAreaProps?.lastNode?.parentNode?.parentNode?.className === 'mi-mention-placeholder') {
      mentionNode = textAreaProps.lastNode.parentNode.parentNode;
    }

    var range = document.createRange();
    range.selectNode(mentionNode);
    var sel = this.getSelection(textArea);
    this.addRangeToSelection(range, sel);
    document.execCommand('delete', false, null);
    this.getCaretPosition(textArea, textAreaProps, chatCloudLinkService, chatLoggingService, componentName);

    this.removeTooltip();
  }

  static removeTooltip() {
    let mentionTooltip = document.querySelector('.mention-tooltip');
    if (mentionTooltip) {
      mentionTooltip.parentElement.removeChild(mentionTooltip);
    }
  }

  static getCaretCoordinates(element, position, chatCloudlinkService: ChatCloudLinkService) {
    //courtesy of https://github.com/component/textarea-caret-position
    // The mirror div will replicate the textarea's style
    var div = document.createElement('div');
    div.id = 'input-textarea-caret-position-mirror-div';
    document.body.appendChild(div);

    var style = div.style;
    var computed = window.getComputedStyle ? window.getComputedStyle(element) : element.currentStyle; // currentStyle for IE < 9
    var isInput = element.nodeName === 'INPUT';

    // Default textarea styles
    style.whiteSpace = 'pre-wrap';
    if (!isInput) style.wordWrap = 'break-word'; // only for textarea-s

    // Position off-screen
    style.position = 'absolute'; // required to return coordinates properly
    style.visibility = 'hidden'; // not 'display: none' because we want rendering

    let properties = [
      'direction', // RTL support
      'boxSizing',
      'width', // on Chrome and IE, exclude the scrollbar, so the mirror div wraps exactly as the textarea does
      'height',
      'overflowX',
      'overflowY', // copy the scrollbar for IE

      'borderTopWidth',
      'borderRightWidth',
      'borderBottomWidth',
      'borderLeftWidth',
      'borderStyle',

      'paddingTop',
      'paddingRight',
      'paddingBottom',
      'paddingLeft',

      // https://developer.mozilla.org/en-US/docs/Web/CSS/font
      'fontStyle',
      'fontVariant',
      'fontWeight',
      'fontStretch',
      'fontSize',
      'fontSizeAdjust',
      'lineHeight',
      'fontFamily',

      'textAlign',
      'textTransform',
      'textIndent',
      'textDecoration', // might not make a difference, but better be safe

      'letterSpacing',
      'wordSpacing',

      'tabSize',
      'MozTabSize'
    ];
    // Transfer the element's properties to the div
    properties.forEach(function (prop) {
      if (isInput && prop === 'lineHeight') {
        // Special case for <input>s because text is rendered centered and line height may be != height
        if (computed.boxSizing === 'border-box') {
          var height = parseInt(computed.height);
          var outerHeight =
            parseInt(computed.paddingTop) +
            parseInt(computed.paddingBottom) +
            parseInt(computed.borderTopWidth) +
            parseInt(computed.borderBottomWidth);
          var targetHeight = outerHeight + parseInt(computed.lineHeight);
          if (height > targetHeight) {
            style.lineHeight = height - outerHeight + 'px';
          } else if (height === targetHeight) {
            style.lineHeight = computed.lineHeight;
          } else {
            style.lineHeight = '0';
          }
        } else {
          style.lineHeight = computed.height;
        }
      } else {
        style[prop] = computed[prop];
      }
    });

    if (chatCloudlinkService.browser.name.toLowerCase() === 'firefox') {
      // Firefox lies about the overflow property for textareas: https://bugzilla.mozilla.org/show_bug.cgi?id=984275
      if (element.scrollHeight > parseInt(computed.height)) style.overflowY = 'scroll';
    } else {
      style.overflow = 'hidden'; // for Chrome to not render a scrollbar; IE keeps overflowY = 'scroll'
    }
    div.textContent = element.innerText.substring(0, position);
    // The second special handling for input type="text" vs textarea:
    // spaces need to be replaced with non-breaking spaces - http://stackoverflow.com/a/13402035/1269037
    if (isInput) div.textContent = div.textContent.replace(/\s/g, '\u00a0');

    var span = document.createElement('span');
    // Wrapping must be replicated *exactly*, including when a long word gets
    // onto the next line, with whitespace at the end of the line before (#7).
    // The  *only* reliable way to do that is to copy the *entire* rest of the
    // textarea's content into the <span> created at the caret position.
    // For inputs, just '.' would be enough, but no need to bother.
    span.textContent = element.innerText.substring(position) || '.'; // || because a completely empty faux span doesn't render at all
    div.appendChild(span);

    var coordinates = {
      top: span.offsetTop + parseInt(computed['borderTopWidth']),
      left: span.offsetLeft + parseInt(computed['borderLeftWidth']),
      height: parseInt(computed['lineHeight'])
    };

    document.body.removeChild(div);

    return coordinates;
  }

  static getCaretPosition(
    textArea: ElementRef<any>,
    textAreaProps: TextAreaProps,
    chatCloudlinkService: ChatCloudLinkService,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    checkForMention = false
  ) {
    textArea?.nativeElement?.focus();
    let sel = this.getSelection(textArea);
    if (sel?.rangeCount > 0) {
      let _range = this.getRange(textArea, sel);
      let range = _range.cloneRange();
      range.selectNodeContents(textArea.nativeElement);
      range.setEnd(_range.endContainer, _range.endOffset);
      textAreaProps.lastCaretPos = range.endOffset;
      textAreaProps.lastNode = range.endContainer;

      //For Firefox
      if (
        checkForMention &&
        chatCloudlinkService.browser.name.toLowerCase() === 'firefox' &&
        textAreaProps?.lastNode?.id === 'text-area' &&
        textAreaProps?.lastNode?.firstChild?.className === 'mi-mention-placeholder'
      ) {
        textAreaProps.lastNode = textAreaProps.lastNode.firstChild;
      }

      //Android bug where it sets the node inside the mention :/
      if (
        textAreaProps.lastNode &&
        textAreaProps.lastNode.parentElement &&
        textAreaProps.lastNode.parentElement.className === 'mention'
      ) {
        chatLoggingService.debug({
          logMessage: componentName + '.getCaretPosition() moved outside to mention element'
        });
        textAreaProps.lastNode = textAreaProps.lastNode.parentElement.parentElement;
      }
    }
  }

  static setCaretPosition(textArea: ElementRef<any>, textAreaProps: TextAreaProps) {
    var node = textArea.nativeElement;
    node.focus();
    var range = document.createRange();
    range.setStart(textAreaProps.lastNode, textAreaProps.lastCaretPos);
    range.setEnd(textAreaProps.lastNode, textAreaProps.lastCaretPos);
    var sel = this.getSelection(textArea);
    this.addRangeToSelection(range, sel);
  }

  static textInputEvent(event, textAreaProps: TextAreaProps) {
    if (
      event.inputType === 'deleteContentBackward' ||
      (event.data &&
        textAreaProps.lastInputString &&
        (event.data === textAreaProps.lastInputString.substring(0, textAreaProps.lastInputString.length - 1) ||
          event.data === textAreaProps.lastInputString))
    ) {
      textAreaProps.lastInputValue = 'Backspace';
    } else if (event.data && event.data.length === 1) {
      textAreaProps.lastInputValue = event.data;
    } else if (
      event.data &&
      event.data.startsWith(textAreaProps.lastInputString) &&
      event.data.length === textAreaProps.lastInputString.length + 1
    ) {
      textAreaProps.lastInputValue = event.data.substring(event.data.length - 1, event.data.length);
    } else {
      textAreaProps.lastInputValue = undefined;
    }
    textAreaProps.lastInputString = event.data;
  }

  static onEmojiClick(
    event,
    textArea: ElementRef<any>,
    textAreaProps: TextAreaProps,
    chatCloudLinkService: ChatCloudLinkService,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    cdRef: ChangeDetectorRef,
    emojiPipe: EmojiPipe,
    isMobile: boolean,
    enableTwemoji: boolean
  ) {
    var emoji = event.native;
    if (!isMobile && enableTwemoji) {
      emoji = event.unicode;
    }
    var node = textArea.nativeElement;
    node.focus();
    var range = document.createRange();
    if (!textAreaProps.lastNode) {
      this.getCaretPosition(textArea, textAreaProps, chatCloudLinkService, chatLoggingService, componentName);
    }
    range.setStart(textAreaProps.lastNode, textAreaProps.lastCaretPos);
    range.setEnd(textAreaProps.lastNode, textAreaProps.lastCaretPos);
    var sel = this.getSelection(textArea);
    this.addRangeToSelection(range, sel);

    if (sel.getRangeAt && sel.rangeCount) {
      range = this.getRange(textArea, sel);
      range.deleteContents();
      if (isMobile || !enableTwemoji) {
        document.execCommand('insertText', false, emoji);
      } else {
        let emojiSvg = emojiPipe.transform(emoji)['changingThisBreaksApplicationSecurity'] as string;
        document.execCommand('InsertHTML', false, emojiSvg);
      }
      this.getCaretPosition(textArea, textAreaProps, chatCloudLinkService, chatLoggingService, componentName);
    }
    node.focus();
    cdRef.detectChanges();
  }

  static convertEmojisToText(
    textArea: ElementRef<any>,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    setMessageFn?: Function
  ): string {
    if (textArea?.nativeElement) {
      //Need to clone to not modify existing element (specifically for edit mode)
      let clonedTextArea = textArea.nativeElement.cloneNode(true);
      var emojiSvg = clonedTextArea.querySelectorAll('.emoji');
      if (emojiSvg.length > 0) {
        emojiSvg.forEach(emoji => {
          let emojiNative = emoji.getAttribute('alt');
          clonedTextArea.replaceChild(document.createTextNode(emojiNative), emoji);
          chatLoggingService.debug({
            logMessage: componentName + '.convertEmojisToText()',
            logParameters: [emojiNative]
          });
        });
        if (setMessageFn) {
          setMessageFn(clonedTextArea.innerText);
        }
      }
      return clonedTextArea.innerText;
    }
  }

  //Transform mentions into text form
  static convertMentionsToText(
    textArea: ElementRef<any>,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    setMessageFn?: Function
  ): string {
    if (textArea?.nativeElement) {
      //Need to clone to not modify existing element (specifically for edit mode)
      let clonedTextArea = textArea.nativeElement.cloneNode(true);
      var mentionPlaceholders = clonedTextArea.querySelectorAll('.mi-mention-placeholder');
      chatLoggingService.debug({
        logMessage: componentName + '.convertMentionsToText() mentionPlaceholders length',
        logParameters: [mentionPlaceholders.length]
      });
      if (mentionPlaceholders.length) {
        for (var i = 0; i < mentionPlaceholders.length; i++) {
          var mentionId = mentionPlaceholders[i].getAttribute('itemprop');
          chatLoggingService.debug({
            logMessage: componentName + '.convertMentionsToText() mentionId',
            logParameters: [mentionId]
          });
          var mentionNode = mentionPlaceholders[i].querySelector('.mention');
          if (mentionNode) {
            mentionNode.innerText = mentionId;
            chatLoggingService.debug({
              logMessage: componentName + '.convertMentionsToText() mentionNode.innerText',
              logParameters: [mentionNode.innerText]
            });
          }
        }
        if (setMessageFn) {
          setMessageFn(clonedTextArea.innerText);
        }
      }
      return clonedTextArea.innerText;
    }
  }

  static checkForEmojis(
    event,
    emojiDictionary,
    textAreaProps: TextAreaProps,
    textArea: ElementRef<any>,
    chatCloudLinkService: ChatCloudLinkService,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    setMessageFn?: Function
  ) {
    var re =
      /(?:[\u00A9\u00AE\u203C\u2049\u2122\u2139\u2194-\u2199\u21A9-\u21AA\u231A-\u231B\u2328\u23CF\u23E9-\u23F3\u23F8-\u23FA\u24C2\u25AA-\u25AB\u25B6\u25C0\u25FB-\u25FE\u2600-\u2604\u260E\u2611\u2614-\u2615\u2618\u261D\u2620\u2622-\u2623\u2626\u262A\u262E-\u262F\u2638-\u263A\u2640\u2642\u2648-\u2653\u2660\u2663\u2665-\u2666\u2668\u267B\u267F\u2692-\u2697\u2699\u269B-\u269C\u26A0-\u26A1\u26AA-\u26AB\u26B0-\u26B1\u26BD-\u26BE\u26C4-\u26C5\u26C8\u26CE-\u26CF\u26D1\u26D3-\u26D4\u26E9-\u26EA\u26F0-\u26F5\u26F7-\u26FA\u26FD\u2702\u2705\u2708-\u270D\u270F\u2712\u2714\u2716\u271D\u2721\u2728\u2733-\u2734\u2744\u2747\u274C\u274E\u2753-\u2755\u2757\u2763-\u2764\u2795-\u2797\u27A1\u27B0\u27BF\u2934-\u2935\u2B05-\u2B07\u2B1B-\u2B1C\u2B50\u2B55\u3030\u303D\u3297\u3299]|(?:\uD83C[\uDC04\uDCCF\uDD70-\uDD71\uDD7E-\uDD7F\uDD8E\uDD91-\uDD9A\uDDE6-\uDDFF\uDE01-\uDE02\uDE1A\uDE2F\uDE32-\uDE3A\uDE50-\uDE51\uDF00-\uDF21\uDF24-\uDF93\uDF96-\uDF97\uDF99-\uDF9B\uDF9E-\uDFF0\uDFF3-\uDFF5\uDFF7-\uDFFF]|\uD83D[\uDC00-\uDCFD\uDCFF-\uDD3D\uDD49-\uDD4E\uDD50-\uDD67\uDD6F-\uDD70\uDD73-\uDD7A\uDD87\uDD8A-\uDD8D\uDD90\uDD95-\uDD96\uDDA4-\uDDA5\uDDA8\uDDB1-\uDDB2\uDDBC\uDDC2-\uDDC4\uDDD1-\uDDD3\uDDDC-\uDDDE\uDDE1\uDDE3\uDDE8\uDDEF\uDDF3\uDDFA-\uDE4F\uDE80-\uDEC5\uDECB-\uDED2\uDEE0-\uDEE5\uDEE9\uDEEB-\uDEEC\uDEF0\uDEF3-\uDEF6]|\uD83E[\uDD10-\uDD1E\uDD20-\uDD27\uDD30\uDD33-\uDD3A\uDD3C-\uDD3E\uDD40-\uDD45\uDD47-\uDD4B\uDD50-\uDD5E\uDD80-\uDD91\uDDC0]))/g;
    var replaced = false;

    if (textAreaProps.lastNode.nodeName === '#text' && event.code == KeyboardKeys.SPACE) {
      emojiDictionary.forEach(d => {
        if (textAreaProps.lastCaretPos > d.key.length && !replaced) {
          if (
            textAreaProps.lastNode.textContent.substring(
              textAreaProps.lastCaretPos - d.key.length - 1,
              textAreaProps.lastCaretPos - 1
            ) == d.key
          ) {
            if (
              textAreaProps.lastCaretPos == d.key.length + 1 ||
              textAreaProps.lastNode.textContent.substring(
                textAreaProps.lastCaretPos - d.key.length - 2,
                textAreaProps.lastCaretPos - d.key.length - 1
              ) === ' '
            ) {
              this.replaceEmoji(
                d,
                textArea,
                textAreaProps,
                chatCloudLinkService,
                chatLoggingService,
                componentName,
                setMessageFn
              );
              replaced = true;
            } else {
              var spaceIndex = textAreaProps.lastNode.textContent
                .substring(0, textAreaProps.lastCaretPos - 1)
                .indexOf(' ');
              var startSearch = 0;
              if (spaceIndex > 0) {
                startSearch = spaceIndex + 1;
              }
              //Need to check if the character before is an emoji
              var stringBefore = textAreaProps.lastNode.textContent.substring(
                startSearch,
                textAreaProps.lastCaretPos - d.key.length - 1
              );
              var result = re.exec(stringBefore);
              if (result) {
                if (
                  stringBefore.substring(stringBefore.length - this.getEmojiValue(d.value).length).charCodeAt(0) ===
                  (<any>result[0]).charCodeAt(0)
                ) {
                  this.replaceEmoji(
                    d,
                    textArea,
                    textAreaProps,
                    chatCloudLinkService,
                    chatLoggingService,
                    componentName,
                    setMessageFn
                  );
                  replaced = true;
                }
              }
            }
          }
        }
      });
    }
  }

  static replaceEmoji(
    emojiEntry,
    textArea: ElementRef<any>,
    textAreaProps: TextAreaProps,
    chatCloudLinkService: ChatCloudLinkService,
    chatLoggingService: ChatLoggingService,
    componentName: string,
    setMessageFn?: Function
  ) {
    var oldMessageLength;
    var newMessageLength;
    this.getCaretPosition(textArea, textAreaProps, chatCloudLinkService, chatLoggingService, componentName);
    oldMessageLength = textAreaProps.lastNode.textContent.length;
    textAreaProps.lastNode.textContent =
      textAreaProps.lastNode.textContent.substring(0, textAreaProps.lastCaretPos - emojiEntry.key.length - 1) +
      this.getEmojiValue(emojiEntry.value) +
      textAreaProps.lastNode.textContent.substring(
        textAreaProps.lastCaretPos - 1,
        textAreaProps.lastNode.textContent.length
      );
    newMessageLength = textAreaProps.lastNode.textContent.length;
    textAreaProps.lastCaretPos -= oldMessageLength - newMessageLength;
    this.setCaretPosition(textArea, textAreaProps);
    if (setMessageFn) {
      setMessageFn(textArea.nativeElement.innerText);
    }
  }

  static getEmojiValue(value) {
    return String.fromCodePoint(parseInt(value, 16));
  }

  /**
   * Programmatically set max-height on PrimeNG menu dropdowns
   * This method directly applies styles to the DOM elements since CSS approaches may fail due to specificity
   */
  static setDropdownMaxHeight(
    menuType: 'commands' | 'mentions' = 'mentions',
    dropdownMaxHeight: string,
    chatLoggingService?: ChatLoggingService
  ): void {
    setTimeout(() => {
      const className = menuType === 'commands' ? '.command-dropdown' : '.mentions-dropdown';
      const overlay = document.querySelector(`${className}.p-menu-overlay`) as HTMLElement;

      if (overlay) {
        const menuList = overlay.querySelector('.p-menu-list') as HTMLElement;
        if (menuList) {
          // Convert rem value to pixels for direct application
          const maxHeightInRem = parseFloat(dropdownMaxHeight);
          const maxHeightInPx = maxHeightInRem * 14;

          // Apply max-height only to menu list to eliminate empty space between menu and text area
          menuList.style.maxHeight = `${maxHeightInPx}px`;
          menuList.style.overflowY = 'auto';

          // Reset overlay container styles to prevent empty space
          overlay.style.maxHeight = 'none';
          overlay.style.height = 'auto';
        }
      } else {
        if (chatLoggingService) {
          chatLoggingService.info({
            logMessage: `MentionHelpers.setDropdownMaxHeight(): ${menuType} overlay not found in DOM`
          });
        }
      }
    }, 10); // Small delay to ensure DOM is rendered
  }
}
